import ExitToAppIcon from "@mui/icons-material/OutputRounded";
import type { Color } from "@mui/material";
import { Box, Button, Divider, LinearProgress, Stack, Typography } from "@mui/material";
import { useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed.tsx";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader.tsx";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer.tsx";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid.ts";
import useClientContext from "../../../../../context/useClientContext.ts";
import GeneralPageHeader from "../../../../common/GeneralPageHeader.tsx";
import ExportOptionsMenu from "../../sv/components/common/export/ExportOptionsMenu.tsx";
import CollapseContainer from "../../sv/components/common/CollapseContainer.tsx";
import RefreshGridButton from "../../sv/components/viewer/common/RefreshGridButton.tsx";
import ExcelTableIcon from "@entrilia/shared/icons/ExcelTableIcon.tsx";
import { useAppDispatch } from "../../sv/store/hooks/useAppDispatch.ts";
import { createDrillDownPageAction } from "../../sv/store/utils/storeUtils.ts";
import StandardReportIconAvatar from "../StandardReportIconAvatar.tsx";
import AgingSummarySettingsPanel from "./components/AgingSummarySettingsPanel.tsx";
import AgingSummaryTable from "./components/AgingSummaryTable.tsx";
import AgingSummaryToolbar from "./components/AgingSummaryToolbar.tsx";
import useAgingSummaryContext from "./contexts/AgingSummaryContext.tsx";

interface Props {
  reportTitle: string;
  reportId: string;
  reportIcon: React.ReactNode;
  reportIconColor: Color;
}

const AgingSummaryView = ({ reportTitle, reportId, reportIcon, reportIconColor }: Props) => {
  const dispatch = useAppDispatch();
  const { title } = useClientContext();
  const {
    report,
    reportError,
    isReportFetching,
    refresh,
    buildProgress,
    deferredEtag,
    exporting,
    exportValuesAsDisplayed,
    onExportValuesAsDisplayedChange,
    onExport,
    onExportWithParameters,
  } = useAgingSummaryContext();
  const [exportMenuAnchor, setExportMenuAnchor] = useState<HTMLElement | null>(null);

  return (
    <Stack spacing={2} sx={{ width: "100%", flex: 1 }}>
      <GeneralPageHeader
        showDefaultBackButtonTo=".."
        title=""
        TitleComponent={
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <StandardReportIconAvatar color={reportIconColor}>{reportIcon}</StandardReportIconAvatar>
            <Divider orientation="vertical" flexItem />
            <Stack>
              <Typography variant="h6">{reportTitle}</Typography>
              <Typography variant="caption" color="textSecondary">
                {title}
              </Typography>
            </Stack>
          </Stack>
        }
      >
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ExitToAppIcon />}
          disabled={exporting}
          loading={exporting}
          onClick={(event) => setExportMenuAnchor(event.currentTarget)}
        >
          Export
        </Button>
      </GeneralPageHeader>
      <ExportOptionsMenu
        open={!!exportMenuAnchor}
        anchorEl={exportMenuAnchor}
        onClose={() => setExportMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        valueSettings={{ value: exportValuesAsDisplayed, onChange: onExportValuesAsDisplayedChange }}
        options={[
          { key: "excel", title: "Excel", Icon: <ExcelTableIcon />, onClick: onExport },
          {
            key: "excel-with-parameters",
            title: "Excel including Parameters",
            Icon: <ExcelTableIcon />,
            onClick: onExportWithParameters,
          },
        ]}
      />
      <Stack sx={{ flex: 1, mt: "0 !important" }}>
        <Box sx={{ px: 2.5, py: 1 }}>
          <AgingSummaryToolbar />
        </Box>
        <Divider orientation="horizontal" flexItem />

        <Stack direction="row" sx={{ flex: 1, minWidth: 0 }}>
          <CollapseContainer
            defaultCollapsed
            hideBorder
            collapseElement={
              <Typography
                variant="h6"
                sx={(theme) => ({ color: theme.palette.text.primary, transform: "rotate(90deg)" })}
              >
                Settings
              </Typography>
            }
          >
            <Box sx={{ p: 2.5 }}>
              <AgingSummarySettingsPanel />
            </Box>
          </CollapseContainer>
          <Divider orientation="vertical" flexItem />
          <Stack direction="column" spacing={1} sx={{ flex: 1, px: 2.5, py: 1, minHeight: 0, position: "relative" }}>
            <Box
              sx={{
                display: buildProgress === undefined ? "none" : "block",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                pointerEvents: "none",
                zIndex: 7,
              }}
            >
              <LinearProgress
                key={deferredEtag}
                variant="determinate"
                value={buildProgress ?? 0}
                sx={{ height: "2px", background: "none", opacity: 0.6 }}
              />
            </Box>
            <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
              <RefreshGridButton onRefresh={refresh} disabled={isReportFetching} />
            </Stack>
            {!report && !!reportError && !isReportFetching && <DataLoadingFailed title="Failed to load report" />}
            {!report && isReportFetching && <InlineLoader />}
            {report && (
              <Stack sx={{ flex: 1, position: "relative", minHeight: 0 }}>
                <ScrollableFlexContainer>
                  <AgingSummaryTable
                    onDrillDown={(cell) => {
                      if (!cell.drillDownId) return;
                      dispatch(
                        createDrillDownPageAction("add", {
                          pageId: generateGuid(),
                          reportId,
                          onlyLedger: true,
                          chartOfAccounts: false,
                          layer: { cell, drillDownId: cell.drillDownId },
                        })
                      );
                    }}
                  />
                </ScrollableFlexContainer>
                {isReportFetching && <InlineLoader overlay />}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default AgingSummaryView;
